import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FiTaskSharedService } from '@fi-shared/fi-task-shared/fi-task-shared.service';
import { ITask, ITaskParameter } from '@fi-shared/fi-task-shared/fi-task-shared';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDateFormatOptions } from '@fi-shared/utils';
import { ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { FiTaskHelperService } from '@fi-shared/fi-task-shared/fi-task-helper.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { IChecklist2Parameter } from '@fi-shared/checklist/checklist-v2';

@Component({
    selector: 'fi-system-notifications-tasks',
    templateUrl: './tasks.component.html',
    styleUrls: ['./tasks.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-system-notifications-tasks' },
    standalone: false,
})
export class SystemNotificationsTasksComponent extends FiRemoteStateFullDataSource<ITask> implements OnInit, OnDestroy {
    defaultSortColumnName: string = 'Deadline';
    defaultSortDirection = false;
    FiStateNames = FiStateNames;
    itemsListType = FiItemsListType.SystemNotificationsTasks;
    objectType = ObjectTypes.Task;
    numberOfRecordsToTake = 10;

    constructor(
        injector: Injector,
        private taskSharedService: FiTaskSharedService,
        private taskHelperService: FiTaskHelperService,
        private fiFormatDateService: FiFormatDateService,
    ) {
        super(injector);
    }

    public ngOnInit(): void {
        // todo, it can be useful if we need to update tasks in notifications on tasks to be done changing
        /*this.taskSharedService.taskIdsToBeDone$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.reloadData();
        });*/
    }

    private _updatedItems: { task: ITask; index: number }[] = [];
    protected completeSelectedTasks(): void {
        const selectedItems = this.selectedItems();
        this._updatedItems = selectedItems.map((item) => ({
            task: item,
            index: this.items().findIndex((t) => t.Id === item.Id),
        }));
        this.taskSharedService
            .updateTaskCompletedStatus(
                selectedItems.map((task) => task.Id),
                true,
            )
            .then(() => {
                this.removeData(selectedItems);
                this.showUndoSnackbar();
            });
    }

    private showUndoSnackbar(): void {
        this.taskHelperService
            .getStatusChangedSnackbar(
                this.taskHelperService.getTasksCompletedSnackbarMessage(this.getUpdatedItemsNames()),
                this.appTextService.get('common.undo'),
            )
            .onAction()
            .subscribe(() => {
                this.restorePreviousStatuses();
            });
    }

    private restorePreviousStatuses(): void {
        this.taskSharedService
            .updateTaskCompletedStatus(
                this._updatedItems.map((item) => item.task.Id),
                false,
            )
            .then(() => {
                const items = this.items();
                this._updatedItems.forEach((item) => {
                    items.splice(item.index, 0, item.task);
                });
                this.setItems(items);
                this.taskHelperService.getStatusChangedSnackbar(
                    this.taskHelperService.getTasksCompletedUndoSnackbarMessage(this.getUpdatedItemsNames()),
                    this.appTextService.get('common.dismiss'),
                );
            });
    }

    private getUpdatedItemsNames(): string[] {
        return this._updatedItems.map((item) => item.task.Name);
    }

    protected getTaskStatusValue(task: ITask): string {
        const isPrivate = this.taskHelperService.isPrivate(task);
        return this.appTextService.get(isPrivate ? 'task.private' : 'task.assigned');
    }

    protected getTaskDeadlineValue(task: ITask): string {
        return this.fiFormatDateService.formatDate(task.Deadline, FiDateFormatOptions);
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<ITask>> {
        return new Promise<IItemsListDataFetchResult<ITask>>((resolve, reject) => {
            const parameter = this.getItemsListParameter(loadArgs) as ITaskParameter;
            this.taskSharedService.getTasksListData(parameter).then((data) => {
                resolve(data);
            }, reject);
        });
    }

    protected getItemsListParameter(loadArgs: ILoadDataArgs): IChecklist2Parameter {
        const parameter = super.getItemsListParameter(loadArgs) as ITaskParameter;
        this.taskHelperService.updateTaskParameter(parameter, {
            filterType: SideListOptionType.TasksToBeDone,
            isArchived: false,
            isCompleted: false,
        });
        return parameter;
    }

    public onDestroy(): void {
        super.ngOnDestroy();
    }
}
